import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { 
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useTranslation } from "react-i18next";

// Define the company type
interface Company {
  id: string;
  name: string;
}

const companyFormSchema = z.object({
  id: z.string(),
  name: z.string().min(2, { message: "Company name must be at least 2 characters" }),
});

type CompanyFormValues = z.infer<typeof companyFormSchema>;

interface EditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CompanyFormValues) => void;
  company: Company;
}

export const EditOrganizationModal = ({ isOpen, onClose, onSubmit, company }: EditOrganizationModalProps) => {
  const { t } = useTranslation();
  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      id: company.id,
      name: company.name,
    },
  });

  const handleSubmit = (values: CompanyFormValues) => {
    onSubmit(values);
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="w-[90vw] max-w-[1200px]">
        <DialogHeader>
          <DialogTitle>{t("admin.modals.editCompany.title")}</DialogTitle>
          <DialogDescription>
            {t("admin.company.description")}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 py-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("companies.createModal.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Inc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("common.save")}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
